import React, { useEffect, useRef, useState } from 'react';
import {
  CardContent,
  Typography,
  TextField,
  InputAdornment,
  Box,
} from '@mui/material';
import { useAppContext } from '../context/AppContext';
import { financialService } from '../utils/api';
import { formatToWords } from '../utils/formatInWords';

// Annual totals from the stored split fields (older records use monthly salary / expense / SIP).
const annualIncome = (s) => (s.salary || 0) * 12 + (s.spouseSalary || 0) * 12 + (s.otherIncome || 0);
const annualExpenses = (s) => (s.monthlyExpense || 0) * 12 + (s.oneTimeYearly || 0);
const annualInvestments = (s) => (s.sip || 0) * 12 + (s.annualInvestments || 0);

// Each annual answer is kept in the existing annual cashflow column; the monthly
// columns are set to 0, so every "monthly x 12 + annual" formula still adds up.
const FIELDS = [
  {
    key: 'income',
    label: 'Annual household income (after taxes)',
    placeholder: 'annual household income',
    aria: 'Annual household income after taxes in rupees',
    total: annualIncome,
    toState: (v) => ({ otherIncome: v, salary: 0, spouseSalary: 0 }),
  },
  {
    key: 'expenses',
    label: 'Annual household expenses',
    placeholder: 'annual household expenses',
    aria: 'Annual household expenses in rupees',
    total: annualExpenses,
    toState: (v) => ({ oneTimeYearly: v, monthlyExpense: 0 }),
  },
  {
    key: 'investments',
    label: 'Annual investments (SIPs and other investments)',
    placeholder: 'annual investments',
    aria: 'Annual investments including SIPs in rupees',
    total: annualInvestments,
    toState: (v) => ({ annualInvestments: v, sip: 0 }),
  },
];

const formatNumber = (num) => (num === '' ? '' : new Intl.NumberFormat('en-IN').format(num));

function HouseholdFinances() {
  const { state, actions } = useAppContext();
  const totalsFrom = (s) => Object.fromEntries(FIELDS.map((f) => [f.key, f.total(s) || '']));
  const [values, setValues] = useState(() => totalsFrom(state));

  // Refresh when saved data arrives after login.
  useEffect(() => {
    setValues(totalsFrom(state));
  }, [state.salary, state.spouseSalary, state.otherIncome, state.monthlyExpense, // eslint-disable-line react-hooks/exhaustive-deps
    state.oneTimeYearly, state.sip, state.annualInvestments]);

  const handleChange = (field) => (e) => {
    const raw = e.target.value.replace(/,/g, '');
    const value = raw === '' ? '' : parseInt(raw, 10) || 0;
    setValues((prev) => ({ ...prev, [field.key]: value }));
    Object.entries(field.toState(value === '' ? 0 : value)).forEach(([k, v]) => actions.updateFormData(k, v));
  };

  // Latest values for the save on unmount.
  const latestRef = useRef(null);
  useEffect(() => {
    latestRef.current = { phoneNumber: state.phoneNumber, state };
  });

  // Save on leaving the card (forward or back), in the annual-column layout.
  useEffect(() => () => {
    const { phoneNumber, state: s } = latestRef.current;
    financialService.submitCashFlow(phoneNumber, {
      salary: 0,
      spouse_salary: 0,
      other_income: annualIncome(s),
      monthly_expense: 0,
      one_time_yearly: annualExpenses(s),
      annual_investments: annualInvestments(s),
      sip: 0,
    }).catch((err) => console.error('submitCashFlow failed:', err));
  }, []);

  return (
    <Box>
      <CardContent>
        <Typography variant="h6" sx={{ mb: 2, color: 'primary.main' }}>
          Your Cashflow
        </Typography>

        {FIELDS.map((field) => (
          <Box key={field.key} sx={{ mt: 2 }}>
            <Typography variant="body1" component="h2" gutterBottom>
              {field.label}
            </Typography>
            <TextField
              inputProps={{ 'aria-label': field.aria }}
              value={formatNumber(values[field.key])}
              onChange={handleChange(field)}
              fullWidth
              type="tel"
              inputMode="numeric"
              placeholder={field.placeholder}
              helperText={values[field.key] ? `₹${formatToWords(values[field.key])}` : ' '}
              InputProps={{
                startAdornment: <InputAdornment position="start">₹</InputAdornment>,
                endAdornment: <InputAdornment position="end">/ year</InputAdornment>,
              }}
            />
          </Box>
        ))}
      </CardContent>
    </Box>
  );
}

export default HouseholdFinances;
